import React, { FC, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useReactiveVar } from '@apollo/client';
import { Alert, Box, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PageTitle from 'components/shared/page-title/page-title.component';
import Loader from 'components/shared/loader/loader.component';
import PriceRequestThread from 'components/price-requests/thread/price-request-thread.component';
import ProductPriceHistory from 'components/price-requests/history/product-price-history.component';
import PriceRequestHeader from 'components/price-requests/request/price-request-header.component';
import PriceRequestLinesTable from 'components/price-requests/request/price-request-lines-table.component';
import PriceRequestResponseGrid from 'components/price-requests/request/price-request-response-grid.component';
import PriceRequestActions from 'components/price-requests/request/price-request-actions.component';
import PriceRequestDraftEditor from 'components/price-requests/request/price-request-draft-editor.component';
import { usePriceRequestResponse } from 'components/price-requests/request/use-price-request-response';
import {
    LAB_PRICE_REQUESTS_URL,
    counterpartOf,
    hasAction,
} from 'components/price-requests/request/price-request-format';
import { priceRequestTemplate } from 'apollo/templates/price-request';
import { PriceRequest, PriceRequestAction, PriceRequestLine, PriceRequestStatus } from 'models/price-request';
import { currentUserVar } from 'apollo/reactive-variables/user';
import { permissions } from 'utils/permission';

/** The loaded request: the response grid's state lives here so the action bar can send it. */
const PriceRequestView: FC<{ request: PriceRequest; onReload: () => Promise<unknown> }> = ({ request, onReload }) => {
    const response = usePriceRequestResponse(request);
    const [historyLine, setHistoryLine] = useState<PriceRequestLine>();
    const canEditPrices = hasAction(request, PriceRequestAction.EDIT_PRICES);
    const isDraft = request.status === PriceRequestStatus.DRAFT;

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) 360px' },
                gap: 3,
                alignItems: 'start',
            }}
        >
            <Box minWidth={0}>
                <PriceRequestHeader request={request} />
                {hasAction(request, PriceRequestAction.EDIT_DRAFT) ? (
                    <PriceRequestDraftEditor request={request} />
                ) : undefined}
                {canEditPrices ? (
                    <PriceRequestResponseGrid request={request} response={response} onOpenHistory={setHistoryLine} />
                ) : isDraft ? undefined : (
                    // A clinic's draft lists its products in the draft editor; the table follows once sent.
                    <PriceRequestLinesTable request={request} onOpenHistory={setHistoryLine} />
                )}
                <PriceRequestActions
                    request={request}
                    response={canEditPrices ? response : undefined}
                    onReload={onReload}
                />
            </Box>
            {/* The thread sits beside the request (below it on narrow screens), like the order chat. */}
            <Box
                sx={{
                    position: { lg: 'sticky' },
                    top: { lg: 88 },
                }}
            >
                <PriceRequestThread priceRequestId={request._id} />
            </Box>
            {historyLine ? (
                <ProductPriceHistory
                    open
                    counterpartId={counterpartOf(request)._id}
                    actionId={historyLine.action}
                    actionSetId={request.actionSet}
                    onClose={() => setHistoryLine(undefined)}
                />
            ) : undefined}
        </Box>
    );
};

/**
 * DD-3697: one price request — header, lines (or the lab's response grid while it may edit
 * prices), the buttons `allowedActions` permits, and the message thread.
 */
const PriceRequestDetails: FC = () => {
    const { t: translate } = useTranslation('price-requests');
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const currentUser = useReactiveVar(currentUserVar);
    // DD-3840: back to the lab's Price adjustments tab, or the chain's own page.
    const listUrl = permissions.isLabTechnician(currentUser?.type) ? LAB_PRICE_REQUESTS_URL : '/price-requests';
    const {
        data,
        loading: isLoading,
        error,
        refetch,
    } = useQuery<{ priceRequest: PriceRequest }>(priceRequestTemplate, {
        variables: { id },
        skip: !id,
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'cache-first',
    });
    const request = data?.priceRequest;

    const renderBody = () => {
        if (error && !request) {
            return (
                <Alert
                    severity="error"
                    action={<Button onClick={() => refetch()}>{translate('RETRY')}</Button>}
                    data-test="price-request.error"
                >
                    {translate('ERROR_LOADING_REQUEST')}
                </Alert>
            );
        }
        if (isLoading && !request) return <Loader />;
        if (!request) return <Alert severity="info">{translate('REQUEST_NOT_FOUND')}</Alert>;
        return <PriceRequestView request={request} onReload={() => refetch()} />;
    };

    return (
        <Box>
            <PageTitle
                headingStyle="h1"
                title={request ? `${translate('PRICE_REQUEST')} ${request.number}` : translate('PRICE_REQUEST')}
                showBackButton
                onBackArrow={() => navigate(listUrl)}
            />
            {renderBody()}
        </Box>
    );
};

export default PriceRequestDetails;
